import { useState, useMemo, useEffect } from 'react';
import { useStakingStore } from '@/store/staking';
import { Event, Page } from '@/config/track-config';
import { scTrack } from '@web3/base-tracking';
import { DappModuleItem, DappLabelItem } from '@/types/staking';
import { useFm } from '@web3/base-hooks';
import { Tabs, Image, Tooltip } from '@bfe/moly';
import cls from 'classnames';

import commonStyles from '@/pages/[locale]/index.module.scss';
import styles from './index.module.less';

// 默认展示8个, 超出横向滚动
const DEFAULT_COUNT = 8;

export const Explore = () => {
  const fm = useFm();
  const {
    configLoading,
    homeConfig: { dappTopicList = [] },
  } = useStakingStore();
  const [currentTab, setCurrentTab] = useState<number | any>();

  const tabList = useMemo(() => {
    if (!dappTopicList) return [];
    return dappTopicList.map((item: { id: number; topic: string }) => {
      const { id, topic } = item;
      return {
        key: id,
        label: <h3>{topic}</h3>,
        children: <div className={styles.tabBox} />,
      };
    });
  }, [dappTopicList]);

  const data = useMemo(() => {
    if (!dappTopicList?.length) return [];
    return (
      dappTopicList.find((item: { id: number }) => item?.id === currentTab)
        ?.dappConfigList || []
    );
  }, [dappTopicList, currentTab]);

  const topicDesc = useMemo(() => {
    if (!dappTopicList?.length) return [];

    return (
      dappTopicList.find((item: { id: number }) => item.id === currentTab)
        ?.topicDesc || []
    );
  }, [dappTopicList, currentTab]);

  const handleTabChange = (val: number | any) => {
    setCurrentTab(val);
    // 埋点
    scTrack(Event.Web3UserClick, {
      page_name: Page.StakingWebHomepage,
      button_name: 'Tab_dappareaname',
      tab_dappname: val,
    });
  };

  const handleClick = (url: string) => {
    if (url) {
      window.top?.open(url, '_blank');
    }
  };

  useEffect(() => {
    // 更新tab栏的默认值
    if (!currentTab && tabList.length) {
      setCurrentTab(tabList[0].key);
    }
  }, [currentTab, tabList]);

  if (!configLoading && !tabList.length) return <></>;

  return (
    <div className={cls(styles.container)}>
      <h2 className={cls(commonStyles.title, styles.title)}>
        {fm('exploreMoreDapp')}
      </h2>
      <Tabs
        size="xl"
        items={tabList}
        activeKey={currentTab}
        onChange={handleTabChange}
        className={commonStyles.tabContainer}
        tabTitleClassName={commonStyles.tabTitleContainer}
      />
      <p className={cls(commonStyles.overflowMultiLine, styles.topicDesc)}>
        {topicDesc}
      </p>

      <div
        className={cls('flex flex-wrap', styles.contentBg, {
          'flex-col': data?.length > DEFAULT_COUNT,
        })}
        key={currentTab}
      >
        {data.map((item: DappModuleItem, index: number) => {
          const { darkIcon, jumpLink, dappDesc, dappName, labels = [] } = item;
          return (
            <div
              key={`${currentTab}${index}`}
              className={cls('flex', styles.box, {
                'cursor-pointer': !!jumpLink,
              })}
              data-spm-even={Event.Web3UserClick}
              data-spm-data={JSON.stringify({
                page_name: Page.StakingWebHomepage,
                button_name: 'Tab_dappareaname',
                dapp_name: name,
              })}
              onClick={() => handleClick(jumpLink)}
            >
              {darkIcon && (
                <Image
                  className={cls('mr-4', styles.icon)}
                  src={darkIcon}
                  style={{
                    height: 48,
                    width: 48,
                  }}
                  alt=""
                />
              )}

              <div className={cls(styles.nameBg)}>
                <div className="flex flex-wrap">
                  <h5
                    className={cls(commonStyles.overflowOneLine, styles.name)}
                  >
                    {dappName}
                  </h5>
                  {/* 标签 */}
                  {Boolean(labels?.length) && (
                    <div
                      className={cls(
                        'flex flex-wrap items-center',
                        styles.tagBg
                      )}
                    >
                      {labels.map((item: DappLabelItem, index) => {
                        const { label, type } = item;
                        return (
                          <span
                            key={`${currentTab}${index}${label}`}
                            className={cls(styles.tag, styles[`tag${type}`])}
                          >
                            {label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className={styles.tooltipWrap}>
                  <Tooltip title={dappDesc}>
                    <p
                      className={cls(
                        'mt-1',
                        commonStyles.overflowOneLine,
                        styles.desc
                      )}
                    >
                      {dappDesc}
                    </p>
                  </Tooltip>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
